import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { getBigAssetPath, getMediumAssetPath } from '@aytm/lib/common/assets'
import { AnswerID } from '@aytm/app/swidget/base/questions/types'
import { EcommerceBannerPlacement, EcommerceResponse, IEcommerceQuestionSchema } from '../../types.ts'
import style from './Main.module.scss'
import ProductCard from './ProductCard.tsx'
import Banner from './Banner.tsx'
import Pagination, { PAGE_SIZE, Showing } from './Pagination.tsx'
import AssetsGate from '../../components/AssetsGate.tsx'
import { drawnCards } from '../../utils/hiddenClass.ts'
import Spinner from './Spinner.tsx'
import filterIcon from '../assets/icons/filter.svg'
import sortIcon from '../assets/icons/sort.svg'

interface IMainProps {
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  registerCardRef: (id: AnswerID, element: HTMLDivElement | null) => void
  onItemSelect: (index: number, bannerPlacement?: EcommerceBannerPlacement) => void
  onAddToCart: (index: number, qnt: number) => void
  page: number
  onPageChange: (page: number) => void
  onBackToTop: () => void
}

const TOOLS = [
  { key: 'filter', icon: filterIcon },
  { key: 'sort', icon: sortIcon }
]

const Main = ({
  schema,
  response,
  registerCardRef,
  onItemSelect,
  onAddToCart,
  page,
  onPageChange,
  onBackToTop
}: IMainProps) => {
  const { t } = useSurveyLocale()
  const viewport = useViewport()

  const topLevel = schema.answers.filter(answer => !answer.na && !answer.parent_product)
  // A banner-flagged product is promoted into the band and leaves the grid. The count and the pages cover
  // the cards the grid draws: a preview-hidden card is left out, a washed-out one stays.
  const products = drawnCards(topLevel.filter(answer => !answer.banner))
  // The band needs both its creative and a product to promote.
  const bannerAnswer = schema.ad_image2 ? topLevel.find(answer => answer.banner) : undefined
  const productsCount = products.length
  const totalPages = Math.max(1, Math.ceil(productsCount / PAGE_SIZE))
  // The page is kept above the grid; a smaller product list clamps it instead of showing an empty page.
  const currentPage = Math.min(page, totalPages)
  // The app lists every result in one scroll; only the desktop grid is paged.
  const pageProducts = viewport.mobile
    ? products
    : products.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
  const productsPerRow = viewport.mobile ? 1 : schema.products_per_row || 5
  const searchTerm = schema.search_condition
  // The banner and the grid wait for their own images, so a page switch reloads the grid only: the
  // banner stays mounted and does not log its impression again. The heading and the toolbar never wait.
  const bannerImages = [bannerAnswer && getBigAssetPath(schema.ad_image2)]
  const pageImages = pageProducts.map(answer => answer.image && getMediumAssetPath(answer.image))

  const grid = (
    <AssetsGate urls={pageImages} loader={<Spinner />}>
      <div
        className={style.grid}
        style={{ gridTemplateColumns: `repeat(${productsPerRow}, minmax(0, 1fr))` }}
        data-testid="SW-EC-Grid"
      >
        {pageProducts.map(answer => (
          <ProductCard
            key={answer.id}
            schema={schema}
            answer={answer}
            amount={response.answers[answer.index].v}
            disabled={'focus' == schema.mode_type && !answer.focused}
            registerCardRef={registerCardRef}
            onSelect={() => onItemSelect(answer.index)}
            onAdd={qnt => onAddToCart(answer.index, qnt)}
          />
        ))}
      </div>
    </AssetsGate>
  )

  const tools = (
    <div className={style.tools}>
      {TOOLS.map(({ key, icon }) => (
        <EmptyLink key={key} color="#00539f">
          <span className={style.tool}>
            <img src={icon} alt="" role="presentation" className={style.toolIcon} />
            {t('questions', 'ec', 'tesco', key)}
          </span>
        </EmptyLink>
      ))}
    </div>
  )

  // The app: the query lives in the top bar, so the list opens on the pills, the count and the banner.
  if (viewport.mobile) {
    return (
      <div className={style.mainMobile} data-testid="SW-EC-Main">
        <div className={style.toolbarMobile}>
          {tools}
          <span className={style.count}>
            {t<(count: number) => string>('questions', 'ec', 'tesco', 'productsCount')(productsCount)}
          </span>
          {bannerAnswer && (
            <AssetsGate urls={bannerImages}>
              <Banner schema={schema} answer={bannerAnswer} onItemSelect={onItemSelect} />
            </AssetsGate>
          )}
        </div>
        {grid}
      </div>
    )
  }

  return (
    <div className={style.main} data-testid="SW-EC-Main">
      {bannerAnswer && (
        <AssetsGate urls={bannerImages}>
          <Banner schema={schema} answer={bannerAnswer} onItemSelect={onItemSelect} />
        </AssetsGate>
      )}
      {searchTerm && (
        <h1
          className={style.resultsTitle}
          dangerouslySetInnerHTML={{
            __html: t<(term: string) => string>('questions', 'ec', 'tesco', 'resultsFor')(searchTerm)
          }}
        />
      )}

      <div className={style.toolbar}>
        {tools}
        <Showing page={currentPage} total={productsCount} />
      </div>

      {grid}
      <Pagination
        page={currentPage}
        totalPages={totalPages}
        total={productsCount}
        onPageChange={onPageChange}
        onBackToTop={onBackToTop}
      />
    </div>
  )
}

export default Main
